import { FC, useRef, forwardRef, useImperativeHandle, useMemo, useState, useContext, useEffect } from 'react';
import V2Tabs from '@/common/components/Data/V2Tabs';
import TaskDetailApproveInfo from '@/common/components/business/ExpandStore/TaskDetailApproveInfo';
import Meet from './components/Meet';
import PointMatching from './components/PointMatching';
import styles from './index.module.less';
import { BusinessMode, TaskStatus } from '@/common/components/business/ExpandStore/ts-config';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { globalAlias, globalFranchiseeAlias } from '@/common/utils/ways';
import UserInfoContext from '@/layout/context';
import CircleBasicInfo from '../CircleBasicInfo';
import FranchiseeInfo from './components/FranchiseeInfo';
import { useMethods } from '@lhb/hook';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import cs from 'classnames';
import V2Flex from '@/common/components/Feedback/V2Flex';
import History from 'src/common/components/business/ExpandStore/CircleTaskDetail/components/History';
import { useTenantCheckInfo } from '@/common/hook/useTenantCheckInfo';
import SpotSurveyTab from './components/SpotSurveyTab';
import TaskTerminateInfo from '@/common/components/business/ExpandStore/TaskTerminateInfo';

enum TabKeys {
  changePoint = 'changePoint', // 机会点
  communication = 'communication', // 沟通记录
  franchisee = 'franchisee',
  spotSurvey = 'spotSurvey', // 踩点
  history = 'history', // 操作历史
}

/**
 * 拓店任务-详情-主体，不含顶部操作按钮
 * @description 加盟+直营A（基诺浦）、加盟+直营B（一鸣）
 * 目前在拓店任务抽屉、拓店异动审批详情、机会点详情的关联任务信息栏使用
 */
const DetailMain: FC<{
  style?,
  /** 拓店任务详情信息 */
  detail,
  /** 是否审批详情内 */
  isApproval?: boolean,
  /** 审批详情 */
  aprDetail?,
  /** 主动刷新页面 */
  refresh,
  /** 是否需要主动隐藏操作按钮 */
  hideOperate?: boolean,
  hideClusterDetail?: boolean,
  isLocxx?: boolean,
  /** 模型同步等弹窗内操作进行中的通知，用于遮罩整个详情弹窗 */
  onOperatingChange?: (loading: boolean) => void,
  ref?: any,
}> = forwardRef(({
  style = {},
  detail, // 拓店任务详情信息
  isApproval = false, // 是否审批详情内
  aprDetail = {}, // 审批详情
  refresh, // 主动刷新页面
  hideOperate = false, // 是否需要主动隐藏操作按钮
  hideClusterDetail = false,
  isLocxx = false,
  onOperatingChange,
}, ref) => {

  // 外部调用异动审批通过操作
  useImperativeHandle(ref, () => ({
    approveTaskChange: (cb: Function) => {
      (approveInfoRef?.current as any).approveTaskChange(cb);
    },
    // 打开机会点详情
    openChancePointDetail: (chancePointId: number | string) => {
      // 切换到机会点tab
      setActive(TabKeys.changePoint);
      // 调用 PointMatching 的方法打开机会点详情
      setTimeout(() => {
        pointMatchingRef.current?.openChancePointDetail?.(chancePointId);
      }, 100);
    },
  }));

  const {
    /** 当前租户的拓店任务模板是否“加盟+直营B（一鸣）”，未确定时为null */
    isStandardAndClusterBTaskTemplate,
  } = useTenantCheckInfo();

  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息

  const [chancePointInfo, setChancePointInfo] = useState<any>({});
  const [franDetail, setFranDetail] = useState<any>({});
  const [active, setActive] = useState<TabKeys>(TabKeys.changePoint);

  const approveInfoRef = useRef<any>(null);
  const pointMatchingRef = useRef<any>(null);
  const spotSurvryRef = useRef<any>(null);
  const meetRef = useRef<any>(null);
  const historyRef = useRef<any>(null);
  /** 展示外部的新增机会点按钮文案，为空则不展示按钮 */
  const [addPointButtonText, setAddPointButtonText] = useState('');
  /** 展示外部的新增沟通记录按钮文案，为空则不展示按钮 */
  const [addMeetButtonText, setAddMeetButtonText] = useState('');
  const [showSpotSurveyExport, setShowSpotSurveyExport] = useState<boolean>(false);
  const [hasSpotSurveyData, setHasSpotSurveyData] = useState<boolean>(false);

  // 是否只能查看不能新增数据（审批详情或者拓店任务状态非进行中时）
  // 手动控制hideOperate
  const isLocxxProcessing = detail?.status === TaskStatus.Processing || detail?.statusName === '进行中';
  const isView = isApproval || hideOperate || (isLocxx ? !isLocxxProcessing : detail.status !== TaskStatus.Processing);

  const chancepointAlias = useMemo(() => {
    return globalAlias(globalAliasItemVOs);
  }, [globalAliasItemVOs]);

  /**
   * @description 业务模式决定是否显示加盟商tab
   * @return
   */
  const tabItems = useMemo(() => {
    let items: any[] = [
      {
        key: TabKeys.changePoint,
        label: `${chancepointAlias} ${chancePointInfo?.objectList?.length || 0}`,
      },
    ];

    if (isLocxx) {
      items = items.concat([{ key: TabKeys.spotSurvey, label: '踩点记录' }, { key: TabKeys.communication, label: '沟通记录' }, { key: TabKeys.history, label: '操作历史' }]);
    } else if (isStandardAndClusterBTaskTemplate) {
      items = items.concat([{
        key: TabKeys.spotSurvey,
        label: '踩点记录'
      },
      {
        key: TabKeys.communication,
        label: `沟通记录`,
      },
      {
        key: TabKeys.history,
        label: '操作历史'
      }]);
    } else {
      items.push({
        key: TabKeys.communication,
        label: `沟通记录`,
      });
    }


    if (!isLocxx && [BusinessMode.Franchisee, BusinessMode.Joint].includes(detail?.businessMode)) {
      if (isStandardAndClusterBTaskTemplate) {
        items.splice(3, 0, {
          key: TabKeys.franchisee,
          label: `${globalFranchiseeAlias(globalAliasItemVOs)}信息`,
        });
      } else {
        items.push({
          key: TabKeys.franchisee,
          label: `${globalFranchiseeAlias(globalAliasItemVOs)}信息`,
        });
      }
    }
    return items;
  }, [chancePointInfo, detail?.businessMode, isStandardAndClusterBTaskTemplate, isLocxx]);

  useEffect(() => {
    // 业务模式已变成直营模式，将不再显示加盟tab，但是选中tab还是加盟信息，需要额外处理下active
    if (detail?.businessMode === BusinessMode.Direct && active === TabKeys.franchisee) {
      setActive(TabKeys.changePoint);
    }
  }, [detail?.businessMode]);

  useEffect(() => {
    // 切走机会点tab时 PointMatching 会卸载，此时收尾遮罩，避免弹窗被永久锁住
    if (active !== TabKeys.changePoint) {
      onOperatingChange?.(false);
    }
  }, [active, onOperatingChange]);

  /**
   * @description 根据加盟商id刷新数据
   */
  useEffect(() => {
    const franId = detail?.standardFranchiseeId;
    // 设置了加盟模式/联营模式，却没有选择加盟商的情况下，会展示加盟信息tab但tab里面不展示数据
    if (!franId) {
      setFranDetail({});
      return;
    }
    methods.getFranDetail(franId);
  }, [detail?.standardFranchiseeId]);

  const methods = useMethods({

    /**
     * @description 获取加盟商详情
     */
    getFranDetail: (id: number) => {
      getFranchiseeDetail({ id }).then((res) => {
        res && setFranDetail(res);
      });
    },

    /**
     * @description 新增机会点
     */
    addChancePoint: () => {
      pointMatchingRef?.current?.addChancePoint();
    },
    /**
     * @description 新增沟通记录
     */
    addCommunicationRecord: () => {
      meetRef?.current?.addCommunicationRecord();
    },
    /**
     * 刷新
     */
    onRefresh() {
      refresh?.();
      historyRef?.current?.refresh?.();
    },
    downLoadSpotSurvey() {
      if (!hasSpotSurveyData) return;
      spotSurvryRef?.current?.handleExport();
    }
  });

  return (
    isNotEmptyAny(detail) ? <div
      className={cs('pt-16 pb-16 pl-40 pr-40', styles.taskDetail)}
      style={{ ...style }}
    >
      <TaskTerminateInfo detail={detail} />

      {/* 异动审批信息 */}
      <TaskDetailApproveInfo
        ref={approveInfoRef}
        detail={detail}
        aprDetail={aprDetail}
        isApproval={isApproval}
      />

      {/* 基本信息 */}
      <CircleBasicInfo
        isApproval={isApproval}
        refresh={methods.onRefresh}
        taskDetail={detail}
        chancePointList={isArray(chancePointInfo?.objectList) ? chancePointInfo?.objectList : []}
        hideClusterDetail={hideClusterDetail}
        hideOperate={hideOperate}
        isLocxx={isLocxx}
      />
      <V2Tabs
        items={tabItems}
        className='mt-16'
        tabBarExtraContent={{
          right: <V2Flex gap={10}>
            {active === TabKeys.changePoint && !isView && addPointButtonText && <a onClick={methods.addChancePoint}>{addPointButtonText}</a>}
            {active === TabKeys.communication && !isView && addMeetButtonText && <a onClick={methods.addCommunicationRecord}>{addMeetButtonText}</a>}
            {active === TabKeys.spotSurvey && showSpotSurveyExport && <a className={!hasSpotSurveyData ? styles.diabledBtn : ''} onClick={methods.downLoadSpotSurvey}>导出</a>}
          </V2Flex>
        }}
        onChange={(value: any) => setActive(value)}
      />

      {/* 匹配点位-机会点 */}
      {active === TabKeys.changePoint && <PointMatching
        ref={pointMatchingRef}
        isLocxx={isLocxx}
        id={detail.id}
        modelClusterId={detail?.modelClusterInfo?.modelClusterId}
        taskDetail={detail}
        // modelClusterInfo={detail?.modelClusterInfo}
        refresh={methods.onRefresh}
        chancepointAlias={chancepointAlias}
        chancePointInfo={chancePointInfo}
        setChancePointInfo={setChancePointInfo}
        hideOperate={hideOperate}
        isView={isView}
        showAddBtn={false}
        setAddPointButtonText={setAddPointButtonText}
        onSyncLoadingChange={onOperatingChange}
      />}

      {/* 沟通记录 */}
      {active === TabKeys.communication && <Meet
        ref={meetRef}
        id={detail.id}
        isView={isView}
        showAddBtn={false}
        setAddMeetButtonText={setAddMeetButtonText}
      />}

      {active === TabKeys.franchisee && <FranchiseeInfo franDetail={franDetail}/>}

      {/* 操作历史 */}
      {active === TabKeys.history ? <History ref={historyRef} id={detail.id}/> : <></>}

      {/* 踩点记录 */}
      {active === TabKeys.spotSurvey ? <SpotSurveyTab setHasData={setHasSpotSurveyData} ref={spotSurvryRef} detail={detail} isLocxx={isLocxx} setShowSpotSurveyExport={setShowSpotSurveyExport}/> : <></>}
    </div> : <></>
  );
});

export default DetailMain;
